import { useEffect, useState } from "react";

import {
  buildInvoiceLogoPreviewStyle,
  loadNaturalRatio,
} from "@/components/features/company-image/invoiceLogoPreview";
import type { Brand } from "@/services/brandService";

export interface BrandLogoPreviewProps {
  brand: Brand;
  /** The tenant's effective invoice logo, used when the brand shares it. */
  fallbackLogoUrl: string | null;
  /** Box representing the LARGEST allowed logo; the preview scales within it. */
  framePx: number;
}

/**
 * The brand's logo rendered through the same crop + size maths the PDF uses,
 * so the settings list shows what the invoice will actually print.
 *
 * A plain <img> would be simpler and wrong: it would ignore the crop, so a
 * tenant who trimmed the whitespace off a wide logo would see the untrimmed
 * version here and the trimmed one on the invoice.
 */
export function BrandLogoPreview({
  brand,
  fallbackLogoUrl,
  framePx,
}: BrandLogoPreviewProps): JSX.Element | null {
  // Null logoImage means the brand SHARES the tenant's logo, not that it has
  // none — so the fallback is part of resolution, not an error path.
  const url = brand.logoImage ?? fallbackLogoUrl;
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!url) {
      setRatio(null);
      return;
    }
    // Guard against a late resolve landing after the row changed logo.
    let cancelled = false;
    void loadNaturalRatio(url).then((r) => {
      if (!cancelled) setRatio(r);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!url) return null;

  // A shared logo is shown whole: the crop stored on the brand was drawn
  // against the brand's own image, and applying it to the tenant's would show
  // an arbitrary region of a different picture.
  const crop = brand.logoImage
    ? {
        x: brand.logoCropX,
        y: brand.logoCropY,
        w: brand.logoCropW,
        h: brand.logoCropH,
        sizePt: brand.logoSizePt,
      }
    : { x: 0, y: 0, w: 1, h: 1, sizePt: brand.logoSizePt };

  const style = buildInvoiceLogoPreviewStyle(url, crop, ratio, framePx);
  if (!style) return null;
  return <div style={style} aria-hidden />;
}
